/**
 * SignupWall — modal that asks the visitor to leave their email so we can
 * send a magic-link to continue the conversation. Two modes:
 *
 *   - `dismissible={true}`  — user clicked "Sign up" themselves; they can close.
 *   - `dismissible={false}` — trial just exhausted; the wall is non-dismissable
 *     (you can't keep talking without auth). The "Stay on the line." copy
 *     is the user-approved warmer variant for the exhausted path.
 */
import { useEffect, useRef, useState } from "react";
import { Mail, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { postEmailStart } from "@/lib/api";

export default function SignupWall({
  open,
  reason = "exhausted",        // "exhausted" | "user_initiated"
  dismissible = false,
  onDismiss,
  onSent,
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorText, setErrorText] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && !sent) {
      // Auto-focus the email input on open.
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open, sent]);

  if (!open) return null;

  const heading = reason === "exhausted" ? "Stay on the line." : "Continue, when you're ready.";
  const body = reason === "exhausted"
    ? "Leave your email and I'll send a quiet link so we can continue."
    : "I'll send a single sign-in link to your email — no password needed.";

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting || !email.trim()) return;
    setSubmitting(true);
    setErrorText("");
    const { ok, status, body } = await postEmailStart(email.trim());
    setSubmitting(false);
    if (!ok) {
      if (status === 429) setErrorText("Just a moment — try again in a minute.");
      else if (status === 400) setErrorText("That doesn't look like a valid email.");
      else setErrorText("Something interrupted that. Try again?");
      return;
    }
    setSent(true);
    onSent?.(body?.email || email.trim());
  };

  return (
    <div
      data-testid="signup-wall"
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center bg-black/30 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-wall-heading"
    >
      <div
        className="relative mx-3 mb-6 w-full max-w-md rounded-3xl bg-[hsl(var(--card))] px-7 py-8 shadow-[0_30px_80px_-20px_rgba(59,74,107,0.35)] sm:mx-0 sm:mb-0"
        data-testid="signup-wall-card"
      >
        {dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid="signup-wall-dismiss"
            aria-label="Close"
            className="absolute right-4 top-4 grid h-7 w-7 place-items-center rounded-full text-muted-foreground/60 transition hover:bg-muted/40 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}

        {!sent ? (
          <>
            <h2
              id="signup-wall-heading"
              data-testid="signup-wall-heading"
              className="text-2xl font-medium tracking-tight text-foreground"
              style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}
            >
              {heading}
            </h2>
            <p
              data-testid="signup-wall-body"
              className="mt-3 text-[14px] leading-relaxed text-muted-foreground"
            >
              {body}
            </p>

            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3">
              <label className="sr-only" htmlFor="signup-wall-email">Email</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                <input
                  ref={inputRef}
                  id="signup-wall-email"
                  data-testid="signup-wall-email-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 w-full rounded-full border border-border/70 bg-background pl-11 pr-4 text-[15px] outline-none transition focus:border-[hsl(var(--accent))]/60 focus:ring-2 focus:ring-[hsl(var(--accent))]/30"
                  required
                />
              </div>

              {errorText && (
                <p
                  data-testid="signup-wall-error"
                  className="text-[12px] text-destructive/80"
                >
                  {errorText}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting || !email.trim()}
                data-testid="signup-wall-submit"
                className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/95 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send me a link"
                )}
              </Button>
            </form>

            <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-foreground/70">
              No password. We'll send a fresh link each time.
            </p>
          </>
        ) : (
          <>
            <h2
              data-testid="signup-wall-sent-heading"
              className="text-2xl font-medium tracking-tight text-foreground"
              style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}
            >
              Check your email.
            </h2>
            <p
              data-testid="signup-wall-sent-body"
              className="mt-3 text-[14px] leading-relaxed text-muted-foreground"
            >
              A sign-in link is on its way to <span className="font-medium text-foreground">{email}</span>.
              It expires in 20 minutes. If you don't see it, peek in your spam folder.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setSent(false); setEmail(""); }}
              data-testid="signup-wall-different-email"
              className="mt-5 text-[12px] text-muted-foreground hover:text-foreground"
            >
              Use a different email
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
